"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Bell, Heart, MessageSquare, UserPlus, X, Check, CheckCheck } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"

const NotificationContainer = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
`

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
`

const NotificationTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.textPrimary};
`

const NotificationBadge = styled.div`
  background-color: ${props => props.theme.error};
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background-color: ${props => props.theme.borderLight};
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const NotificationList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${props => props.$unread ? props.theme.borderLight : props.theme.cardBackground};
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  &:last-child {
    border-bottom: none;
  }
`

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &.like {
    background-color: ${props => props.theme.isDarkMode ? props.theme.borderLight : '#fecaca'};
    color: ${props => props.theme.error};
  }
  
  &.comment {
    background-color: ${props => props.theme.isDarkMode ? props.theme.borderLight : '#bfdbfe'};
    color: ${props => props.theme.buttonPrimary};
  }
  
  &.follow {
    background-color: ${props => props.theme.isDarkMode ? props.theme.borderLight : '#c7d2fe'};
    color: ${props => props.theme.info};
  }
  
  &.mention {
    background-color: ${props => props.theme.isDarkMode ? props.theme.borderLight : '#fbbf24'};
    color: ${props => props.theme.warning};
  }
`

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`

const NotificationText = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 4px;
  line-height: 1.4;
  
  strong {
    font-weight: 600;
  }
`

const NotificationTime = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 8px;
`

const NotificationPreview = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textMuted};
  background-color: ${props => props.theme.hoverBackground};
  padding: 8px;
  border-radius: 4px;
  border-left: 3px solid ${props => props.theme.border};
  margin-top: 8px;
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const NotificationActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const NotificationButton = styled.button`
  padding: 4px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  &.primary {
    background-color: ${props => props.theme.buttonPrimary};
    color: white;
    border-color: ${props => props.theme.buttonPrimary};
    
    &:hover {
      background-color: ${props => props.theme.buttonPrimaryHover};
    }
  }
`

const NotificationDismiss = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    color: ${props => props.theme.textSecondary};
    background-color: ${props => props.theme.hoverBackground};
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.textSecondary};
`

const LoadingState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.textSecondary};
`

// 날짜 포맷 함수
const formatNotificationTime = (dateString) => {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffHour < 24) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;
  
  return `${date.getFullYear()}.${date.getMonth()+1}.${date.getDate()}`;
};

export default function NotificationPanel() {
  const { isLoggedIn } = useAuth()
  const { theme } = useTheme()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  // 알림 목록 로드
  const fetchNotifications = async () => {
    if (!isLoggedIn) return;
    
    setLoading(true)
    try {
      const [notificationsResponse, unreadResponse] = await Promise.all([
        fetch('/notification/list?limit=20', { credentials: 'include' }),
        fetch('/notification/unread-count', { credentials: 'include' })
      ])
      
      const notificationsData = await notificationsResponse.json()
      const unreadData = await unreadResponse.json()
      
      if (notificationsData.success) {
        setNotifications(notificationsData.notifications)
        setUnreadCount(notificationsData.unreadCount || 0)
      }
      
      if (unreadData.success) {
        setUnreadCount(unreadData.count)
      }
    } catch (error) {
      console.error('알림 로드 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  // 컴포넌트 마운트 시 알림 로드
  useEffect(() => {
    if (isLoggedIn) {
      fetchNotifications()
      
      // 30초마다 알림 새로고침
      const interval = setInterval(fetchNotifications, 30000)
      return () => clearInterval(interval)
    }
  }, [isLoggedIn])

  // 알림 읽음 처리
  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`/notification/read/${notificationId}`, {
        method: 'PUT',
        credentials: 'include'
      })
      
      const data = await response.json()
      if (data.success) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, is_read: true }
              : notif
          )
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('알림 읽음 처리 오류:', error)
    }
  }

  // 모든 알림 읽음 처리
  const markAllAsRead = async () => {
    try {
      const response = await fetch('/notification/read-all', {
        method: 'PUT',
        credentials: 'include'
      })
      
      const data = await response.json()
      if (data.success) {
        setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })))
        setUnreadCount(0)
      }
    } catch (error) {
      console.error('모든 알림 읽음 처리 오류:', error)
    }
  }

  // 알림 삭제
  const dismissNotification = async (notificationId) => {
    try {
      const response = await fetch(`/notification/delete/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      
      const data = await response.json()
      if (data.success) {
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId))
        setUnreadCount(prev => {
          const notification = notifications.find(n => n.id === notificationId)
          return notification && !notification.is_read ? Math.max(0, prev - 1) : prev
        })
      }
    } catch (error) {
      console.error('알림 삭제 오류:', error)
    }
  }

  // 알림 클릭 핸들러
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
    
    // 알림 타입에 따른 네비게이션
    if (notification.post_id) {
      // 게시물 상세 페이지로 이동 (구현 필요)
      console.log('Navigate to post:', notification.post_id)
    }
  }

  // 알림 아이콘 렌더링
  const renderNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return <Heart size={20} />
      case 'comment':
      case 'reply':
        return <MessageSquare size={20} />
      case 'follow':
        return <UserPlus size={20} />
      default:
        return <Bell size={20} />
    }
  }

  // 알림 텍스트 렌더링
  const renderNotificationText = (notification) => {
    const actorName = notification.actor_username || '알 수 없는 사용자'
    
    switch (notification.type) {
      case 'like':
        return <><strong>{actorName}</strong>님이 회원님의 게시물을 좋아합니다.</>
      case 'comment':
        return <><strong>{actorName}</strong>님이 회원님의 게시물에 댓글을 달았습니다.</>
      case 'reply':
        return <><strong>{actorName}</strong>님이 회원님의 댓글에 답글을 달았습니다.</>
      case 'follow':
        return <><strong>{actorName}</strong>님이 회원님을 팔로우하기 시작했습니다.</>
      case 'mention':
        return <><strong>{actorName}</strong>님이 회원님을 언급했습니다.</>
      default:
        return notification.content || '새로운 알림이 있습니다.'
    }
  }

  if (!isLoggedIn) {
    return (
      <NotificationContainer theme={theme}>
        <EmptyState theme={theme}>
          <Bell size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p>알림을 보려면 로그인이 필요합니다.</p>
        </EmptyState>
      </NotificationContainer>
    )
  }

  return (
    <NotificationContainer theme={theme}>
      <NotificationHeader theme={theme}>
        <NotificationTitle theme={theme}>
          <Bell size={20} />
          알림
          {unreadCount > 0 && <NotificationBadge theme={theme}>{unreadCount}</NotificationBadge>}
        </NotificationTitle>
        <HeaderActions>
          {unreadCount > 0 && (
            <HeaderButton theme={theme} onClick={markAllAsRead}>
              <CheckCheck size={14} />
              모두 읽음
            </HeaderButton>
          )}
        </HeaderActions>
      </NotificationHeader>

      <NotificationList>
        {loading ? (
          <LoadingState theme={theme}>알림을 불러오는 중...</LoadingState>
        ) : notifications.length === 0 ? (
          <EmptyState theme={theme}>
            <Bell size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <p>새로운 알림이 없습니다.</p>
          </EmptyState>
        ) : (
          notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              theme={theme}
              $unread={!notification.is_read}
              onClick={() => handleNotificationClick(notification)}
            >
              <NotificationIcon theme={theme} className={notification.type}>
                {renderNotificationIcon(notification.type)}
              </NotificationIcon>
              
              <NotificationContent>
                <NotificationText theme={theme}>
                  {renderNotificationText(notification)}
                </NotificationText>
                
                <NotificationTime theme={theme}>
                  {formatNotificationTime(notification.created_at)}
                </NotificationTime>
                
                {(notification.post_content || notification.comment_content) && (
                  <NotificationPreview theme={theme}>
                    {notification.post_content || notification.comment_content}
                  </NotificationPreview>
                )}
                
                {notification.type === 'follow' && (
                  <NotificationActions>
                    <NotificationButton theme={theme} className="primary">
                      팔로우 하기
                    </NotificationButton>
                  </NotificationActions>
                )}
              </NotificationContent>
              
              <NotificationDismiss 
                theme={theme}
                onClick={(e) => {
                  e.stopPropagation()
                  dismissNotification(notification.id)
                }}
              >
                <X size={16} />
              </NotificationDismiss>
            </NotificationItem>
          ))
        )}
      </NotificationList>
    </NotificationContainer>
  )
}